import React, { useState, useEffect, useRef, useCallback } from 'react';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8001';

const getToken = () => localStorage.getItem('policylink_token');

const authFetch = async (url, options = {}) => {
  const token = getToken();
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${token}`,
  };
  const response = await fetch(url, { ...options, headers });
  if (response.status === 401) {
    localStorage.removeItem('policylink_token');
    localStorage.removeItem('policylink_user');
    window.location.reload();
    throw new Error('Session expired');
  }
  return response;
};

// ============================================
// CLIENT ADMIN PANEL
// ============================================

function ClientAdmin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(null);
  const [showResetPassword, setShowResetPassword] = useState(null);
  const [showEditSources, setShowEditSources] = useState(null);

  // Form states
  const [newUser, setNewUser] = useState({ email: '', name: '', can_view_claims: true, source_ids: null });
  const [editUserData, setEditUserData] = useState({});
  const [resetPasswordData, setResetPasswordData] = useState('');
  const [userSources, setUserSources] = useState([]);
  const [createSources, setCreateSources] = useState([]); // sources shown during user creation
  const [saving, setSaving] = useState(false);

  // Prevent modal close when dragging text selection past modal border
  const mouseDownTarget = useRef(null);
  const handleOverlayMouseDown = useCallback((e) => { mouseDownTarget.current = e.target; }, []);
  const handleOverlayClick = useCallback((e, closeFn) => {
    if (e.target === e.currentTarget && mouseDownTarget.current === e.currentTarget) {
      closeFn();
    }
    mouseDownTarget.current = null;
  }, []);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(`${API_BASE}/api/manage/users`);
      if (!res.ok) throw new Error('Failed to fetch users');
      const data = await res.json();
      setUsers(data);
    } catch (err) {
      setError(err.message);
    }
    setLoading(false);
  };

  const fetchUserSources = async (userId) => {
    try {
      const res = await authFetch(`${API_BASE}/api/manage/users/${userId}/sources`);
      if (!res.ok) throw new Error('Failed to fetch source assignments');
      const data = await res.json();
      setUserSources(data);
    } catch (err) {
      setError(err.message);
    }
  };

  // Fetch all tenant sources for the create user form (use any existing user's sources endpoint or a special approach)
  const fetchTenantSourcesForCreate = async () => {
    try {
      // We use the sources API to get all tenant sources
      const res = await authFetch(`${API_BASE}/api/sources`);
      if (!res.ok) throw new Error('Failed to fetch sources');
      const data = await res.json();
      // Mark all as selected by default
      setCreateSources(data.map(s => ({ ...s, assigned: true })));
    } catch (err) {
      setError(err.message);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Build source_ids from the createSources selection
      const selectedSourceIds = createSources.filter(s => s.assigned).map(s => s.id);
      const payload = {
        email: newUser.email,
        name: newUser.name,
        can_view_claims: newUser.can_view_claims,
        source_ids: selectedSourceIds,
      };

      const res = await authFetch(`${API_BASE}/api/manage/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to create user');
      }
      setShowCreateUser(false);
      setNewUser({ email: '', name: '', can_view_claims: true, source_ids: null });
      setCreateSources([]);
      await fetchUsers();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!showEditUser) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/api/manage/users/${showEditUser.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editUserData),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to update user');
      }
      setShowEditUser(null);
      setEditUserData({});
      await fetchUsers();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!showResetPassword) return;
    setSaving(true);
    try {
      const res = await authFetch(`${API_BASE}/api/manage/users/${showResetPassword.id}/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ new_password: resetPasswordData }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to reset password');
      }
      setShowResetPassword(null);
      setResetPasswordData('');
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const handleSaveSources = async () => {
    if (!showEditSources) return;
    setSaving(true);
    try {
      const selectedIds = userSources.filter(s => s.assigned).map(s => s.id);
      const res = await authFetch(`${API_BASE}/api/manage/users/${showEditSources.id}/sources`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source_ids: selectedIds }),
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Failed to update source access');
      }
      setShowEditSources(null);
      setUserSources([]);
      await fetchUsers();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const openEditUser = (user) => {
    setShowEditUser(user);
    setEditUserData({
      name: user.name,
      is_active: user.is_active,
      can_view_claims: user.can_view_claims,
    });
  };

  const openEditSources = async (user) => {
    setShowEditSources(user);
    await fetchUserSources(user.id);
  };

  const openCreateUser = async () => {
    setShowCreateUser(true);
    setNewUser({ email: '', name: '', can_view_claims: true, source_ids: null });
    await fetchTenantSourcesForCreate();
  };

  const toggleCreateSource = (sourceId) => {
    setCreateSources(prev =>
      prev.map(s => s.id === sourceId ? { ...s, assigned: !s.assigned } : s)
    );
  };

  const toggleUserSource = (sourceId) => {
    setUserSources(prev =>
      prev.map(s => s.id === sourceId ? { ...s, assigned: !s.assigned } : s)
    );
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  if (loading && users.length === 0) {
    return (
      <div className="admin-page">
        <div className="admin-loading">
          <div className="spinner"></div>
          <p>Loading user data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="admin-content admin-content-full">
        {error && (
          <div className="admin-error">
            {error}
            <button onClick={() => setError(null)} className="error-close">&times;</button>
          </div>
        )}

        <div className="admin-content-header">
          <div className="admin-content-header-left">
            <h2>User Management</h2>
            <span className="admin-count">{users.length} user{users.length !== 1 ? 's' : ''}</span>
          </div>
          <button className="admin-btn-primary" onClick={openCreateUser}>
            + Create User
          </button>
        </div>

        {users.length === 0 ? (
          <div className="admin-empty">No users found. Create your first user to get started.</div>
        ) : (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Claims</th>
                <th>Sources</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} className={!u.is_active ? 'row-inactive' : ''}>
                  <td className="td-name">{u.name}</td>
                  <td className="td-email">{u.email}</td>
                  <td>
                    <span className={`admin-role-badge role-${u.role}`}>{u.role}</span>
                  </td>
                  <td>
                    <span className={`admin-perm-badge ${u.can_view_claims ? 'perm-yes' : 'perm-no'}`}>
                      {u.can_view_claims ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td>
                    <button className="admin-btn-link" onClick={() => openEditSources(u)}>
                      {u.source_count} source{u.source_count !== 1 ? 's' : ''}
                    </button>
                  </td>
                  <td>
                    <span className={`admin-status-badge ${u.is_active ? 'status-active' : 'status-inactive'}`}>
                      {u.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="td-date">{formatDate(u.last_login)}</td>
                  <td className="td-actions">
                    {u.role !== 'super_admin' && u.role !== 'admin' && (
                      <>
                        <button className="admin-btn-link" onClick={() => openEditUser(u)}>Edit</button>
                        <button className="admin-btn-link" onClick={() => { setShowResetPassword(u); setResetPasswordData(''); }}>Password</button>
                      </>
                    )}
                    {u.role === 'admin' && (
                      <span className="td-note">Admin</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* CREATE USER MODAL */}
      {showCreateUser && (
        <div className="admin-modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={(e) => handleOverlayClick(e, () => setShowCreateUser(false))}>
          <div className="admin-modal admin-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Create User</h3>
              <button className="admin-modal-close" onClick={() => setShowCreateUser(false)}>&times;</button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="admin-modal-body">
                <div className="admin-modal-columns">
                  {/* Left column - user details */}
                  <div className="admin-modal-col">
                    <div className="admin-field">
                      <label>Full Name</label>
                      <input
                        type="text"
                        value={newUser.name}
                        onChange={e => setNewUser({ ...newUser, name: e.target.value })}
                        placeholder="e.g. Jane Smith"
                        required
                        autoFocus
                      />
                    </div>
                    <div className="admin-field">
                      <label>Email</label>
                      <input
                        type="email"
                        value={newUser.email}
                        onChange={e => setNewUser({ ...newUser, email: e.target.value })}
                        placeholder="jane@company.co.za"
                        required
                      />
                    </div>
                    <div className="admin-field-row">
                      <label className="admin-checkbox">
                        <input
                          type="checkbox"
                          checked={newUser.can_view_claims}
                          onChange={e => setNewUser({ ...newUser, can_view_claims: e.target.checked })}
                        />
                        <span>Can View Claims</span>
                      </label>
                    </div>
                  </div>

                  {/* Right column - source access */}
                  <div className="admin-modal-col">
                    <div className="admin-sources-header">
                      <label>Source Access</label>
                      <div className="admin-sources-actions">
                        <button type="button" className="admin-btn-link" onClick={() => setCreateSources(prev => prev.map(s => ({ ...s, assigned: true })))}>Select All</button>
                        <button type="button" className="admin-btn-link" onClick={() => setCreateSources(prev => prev.map(s => ({ ...s, assigned: false })))}>Deselect All</button>
                      </div>
                    </div>
                    <div className="admin-source-list">
                      {createSources.length === 0 ? (
                        <div className="admin-empty-sm">No sources available</div>
                      ) : (
                        createSources.map(s => (
                          <label key={s.id} className="admin-source-item">
                            <input
                              type="checkbox"
                              checked={s.assigned}
                              onChange={() => toggleCreateSource(s.id)}
                            />
                            <span className="admin-source-name">{s.name}</span>
                            {!s.is_enabled && <span className="admin-badge-inactive">Disabled</span>}
                          </label>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowCreateUser(false)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={saving}>
                  {saving ? 'Creating...' : 'Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT USER MODAL */}
      {showEditUser && (
        <div className="admin-modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={(e) => handleOverlayClick(e, () => setShowEditUser(null))}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit User</h3>
              <span className="admin-modal-subtitle">{showEditUser.email}</span>
              <button className="admin-modal-close" onClick={() => setShowEditUser(null)}>&times;</button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="admin-modal-body">
                <div className="admin-field">
                  <label>Name</label>
                  <input
                    type="text"
                    value={editUserData.name || ''}
                    onChange={e => setEditUserData({ ...editUserData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="admin-field-row">
                  <label className="admin-checkbox">
                    <input
                      type="checkbox"
                      checked={editUserData.is_active !== false}
                      onChange={e => setEditUserData({ ...editUserData, is_active: e.target.checked })}
                    />
                    <span>Active</span>
                  </label>
                  <label className="admin-checkbox">
                    <input
                      type="checkbox"
                      checked={editUserData.can_view_claims !== false}
                      onChange={e => setEditUserData({ ...editUserData, can_view_claims: e.target.checked })}
                    />
                    <span>Can View Claims</span>
                  </label>
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowEditUser(null)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* RESET PASSWORD MODAL */}
      {showResetPassword && (
        <div className="admin-modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={(e) => handleOverlayClick(e, () => setShowResetPassword(null))}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Reset Password</h3>
              <span className="admin-modal-subtitle">{showResetPassword.name} ({showResetPassword.email})</span>
              <button className="admin-modal-close" onClick={() => setShowResetPassword(null)}>&times;</button>
            </div>
            <form onSubmit={handleResetPassword}>
              <div className="admin-modal-body">
                <div className="admin-field">
                  <label>New Password</label>
                  <input
                    type="password"
                    value={resetPasswordData}
                    onChange={e => setResetPasswordData(e.target.value)}
                    placeholder="Minimum 8 characters"
                    required
                    minLength={8}
                    autoFocus
                  />
                </div>
              </div>
              <div className="admin-modal-footer">
                <button type="button" className="admin-btn-secondary" onClick={() => setShowResetPassword(null)}>Cancel</button>
                <button type="submit" className="admin-btn-primary" disabled={saving}>
                  {saving ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT SOURCE ACCESS MODAL */}
      {showEditSources && (
        <div className="admin-modal-overlay" onMouseDown={handleOverlayMouseDown} onClick={(e) => handleOverlayClick(e, () => setShowEditSources(null))}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Source Access</h3>
              <span className="admin-modal-subtitle">{showEditSources.name} ({showEditSources.email})</span>
              <button className="admin-modal-close" onClick={() => setShowEditSources(null)}>&times;</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-sources-header">
                <label>Assigned Sources</label>
                <div className="admin-sources-actions">
                  <button type="button" className="admin-btn-link" onClick={() => setUserSources(prev => prev.map(s => ({ ...s, assigned: true })))}>Select All</button>
                  <button type="button" className="admin-btn-link" onClick={() => setUserSources(prev => prev.map(s => ({ ...s, assigned: false })))}>Deselect All</button>
                </div>
              </div>
              <div className="admin-source-list">
                {userSources.length === 0 ? (
                  <div className="admin-empty-sm">Loading sources...</div>
                ) : (
                  userSources.map(s => (
                    <label key={s.id} className="admin-source-item">
                      <input
                        type="checkbox"
                        checked={s.assigned}
                        onChange={() => toggleUserSource(s.id)}
                      />
                      <span className="admin-source-name">{s.name}</span>
                      {!s.is_enabled && <span className="admin-badge-inactive">Disabled</span>}
                    </label>
                  ))
                )}
              </div>
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn-secondary" onClick={() => setShowEditSources(null)}>Cancel</button>
              <button type="button" className="admin-btn-primary" onClick={handleSaveSources} disabled={saving}>
                {saving ? 'Saving...' : 'Save Source Access'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ClientAdmin;
